import { useQuery } from '@tanstack/react-query';

import { Alert } from '../../components/ui/Alert';
import { LinkButton } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DiscoveryPulse } from '../../components/ui/Loader';
import { useSession } from '../../stores/session';
import { ArtistRail } from './ArtistRail';
import { FeaturedHero } from './FeaturedHero';
import { LiveHosts } from './LiveHosts';
import { NewMusicToday } from './NewMusicToday';
import { BecauseYouSaved } from './SidePanels';
import { dashboard } from './api';

/**
 * The signed-in home (from the mockup in `dev/design`).
 *
 * ## Two requests, not one and not seven
 *
 * `/dashboard` carries everything stable for a visit - the hero, the artist
 * rail, the recommendations, the reach figures. `/dashboard/tracks` carries the
 * tabbed strip, which is refetched every time somebody presses a tab. Folding
 * them together would refetch the entire page on a tab press; splitting per
 * section would be seven round trips for one screen.
 *
 * ## Where the layout differs from the mockup, and why
 *
 * - **The live-hosts panel is a placeholder.** Rooms are Phase 7 and no table
 *   backs them, so the slot is held and labelled rather than filled with four
 *   invented rooms. See `LiveHosts`.
 * - **"Heard in rooms" is not a tab**, for the same reason.
 * - **The recommendations carry no match percentage.** Nothing computes one.
 *   See `BecauseYouSaved`.
 *
 * Everything else on this page is a figure some query actually read.
 */
export function DashboardPage() {
  const user = useSession((state) => state.user);


  const { data, isPending, isError } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => dashboard.summary(),
  });

  return (
    <div className="mx-auto flex max-w-[1500px] flex-col gap-12 px-6 py-8" data-dashboard>
      {user && !user.email_verified && (
        <Alert tone="info">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>Confirm your email address to start uploading.</span>
            <LinkButton to="/verify-email" variant="secondary">
              Confirm now
            </LinkButton>
          </div>
        </Alert>
      )}

      {/* ------------------------------------------------------------- */}
      {/* The feature, and the room that is not open yet                 */}
      {/* ------------------------------------------------------------- */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {isPending ? (
          <Card className="flex min-h-[18rem] flex-col items-center justify-center gap-5 p-6">
            {/* The discovery pulse rather than the page loader: this slot is the
                site looking for something on your behalf, which is a different
                promise from "the page is loading". */}
            <DiscoveryPulse />
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-faint">
              Finding something for you
            </span>
          </Card>
        ) : isError ? (
          <Card className="flex min-h-[18rem] items-center p-6">
            <p className="text-sm text-soft">That could not be loaded. Try reloading the page.</p>
          </Card>
        ) : data?.featured ? (
          <FeaturedHero track={data.featured} />
        ) : (
          /*
           * A genuinely empty site, which is a real state on day one rather
           * than an error. It gets an invitation instead of a shrug.
           */
          <Card className="flex min-h-[18rem] flex-col items-start justify-center gap-4 p-8">
            <h2 className="text-2xl font-semibold tracking-tight text-ink">
              Nothing has been released yet.
            </h2>
            <p className="max-w-md text-soft">
              This is where the newest track on the site will be. It could be yours.
            </p>
            {/* /upload needs an account and a confirmed address, so sending a
                stranger there lands them on a sign-in wall one click after an
                invitation. */}
            <LinkButton to={user ? '/upload' : '/register'}>
              {user ? 'Drop a track' : 'Join and drop a track'}
            </LinkButton>
          </Card>
        )}

        <LiveHosts />
      </div>

      {/* ------------------------------------------------------------- */}
      {/* New music today                                                */}
      {/* ------------------------------------------------------------- */}
      <NewMusicToday />

      {/* ------------------------------------------------------------- */}
      {/* Who to find, what to play next, and how you are doing          */}
      {/* ------------------------------------------------------------- */}
      {/* ------------------------------------------------------------- */}
      {/* Who to find, and what to play next                            */}
      {/* ------------------------------------------------------------- */}
      {data && data.undiscovered.length > 0 && <ArtistRail artists={data.undiscovered} />}

      {/*
        A recommendation, in the column with the rest of the discovery.

        It used to sit in a right rail beside two things that were not
        recommendations at all - a reach figure and a boost balance - and the
        three of them shared a third of the width to do three unrelated jobs.
        The figures moved to `/stats`, the balance is in the account menu on
        every page, and this belongs here because finding something to play is
        what this page is for.
      */}
      {data?.recommended && <BecauseYouSaved recommended={data.recommended} />}

    </div>
  );
}
